import { cn } from "@babackapp/customer-ui/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";
import { motion, Variants } from "motion/react";
import { createContext, use, useState } from "react";
import { AnimatedList, AnimatedListItem } from "./animated-list";

const Context = createContext({
  hover: false,
  active: false,
});

export function ArticleList({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AnimatedList> & {
  size?: "default" | "sm";
}) {
  return (
    <AnimatedList
      data-size={size}
      layout
      className={cn(
        "group/articles-list bg-background z-50 w-full flex flex-col border [&>*]:py-4 divide-y rounded-lg px-4",
        size === "sm" && "px-2 [&>*]:py-2 [&_[data-slot=article-image]]:size-8",
        className,
      )}
      {...props}
    />
  );
}

export function Article({
  className,
  disabled,
  ...props
}: React.ComponentProps<typeof AnimatedListItem> & { disabled?: boolean }) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);
  return (
    <Context.Provider value={{ hover, active }}>
      <AnimatedListItem
        layout
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onMouseDown={() => setActive(true)}
        onMouseUp={() => setActive(false)}
        disabled={disabled}
        data-disabled={disabled}
        className={cn(
          "group/article grid grid-cols-[auto_1fr_auto] gap-x-3 w-full bg-background",
          className,
        )}
        {...props}
      />
    </Context.Provider>
  );
}

export function ArticleChildren({
  className,
  ...props
}: React.ComponentProps<typeof motion.div>) {
  return (
    <motion.div
      layout
      className={cn("col-start-1 col-span-3 mt-4", className)}
      {...props}
    />
  );
}

export function ArticleTitle({
  className,
  ...props
}: React.ComponentProps<typeof motion.span>) {
  return (
    <motion.span
      layout="position"
      className={cn(
        "col-start-2 row-start-1 font-sans font-medium text-sm group-data-[size=sm]/articles-list:text-xs",
        className,
      )}
      {...props}
    />
  );
}

export function ArticleSubtitle({
  className,
  ...props
}: React.ComponentProps<typeof motion.span>) {
  return (
    <motion.span
      className={cn(
        "col-start-2 row-start-2 font-content text-xs text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function ArticleFooter({
  className,
  ...props
}: React.ComponentProps<typeof motion.span>) {
  return (
    <motion.span
      className={cn(
        "col-start-2 col-span-2 mt-2 row-start-3 font-content text-xs text-muted-foreground",
        className,
      )}
      {...props}
    />
  );
}

export function ArticleImage({
  className,
  src,
  ...props
}: React.ComponentProps<typeof motion.img>) {
  return (
    <div
      data-slot="article-image"
      className={cn(
        "[&>img]:object-cover overflow-hidden translate-y-0.5 size-10 group-data-[disabled=true]/article:grayscale drop-shadow-lg flex col-start-1 row-start-1 row-span-3",

        className,
      )}
    >
      <motion.img
        src={src}
        layoutId={src}
        transition={{ type: "spring", bounce: 0.3 }}
        className={cn("rounded-md size-full")}
        {...props}
      />
    </div>
  );
}

export function ArticleImagesStack({
  className,
  srcs,
  ids,
  grid,
  layoutIdPrefix,
  ...props
}: Omit<React.ComponentProps<"div">, "src"> & {
  srcs: string[];
  ids?: string[];
  grid?: boolean;
  layoutIdPrefix?: string;
}) {
  const { hover, active } = use(Context);

  const imageVariants: Variants[] = [
    {
      initial: {
        rotate: grid ? 0 : -20,
        x: grid ? 0 : -8,
        y: 0,
        scale: grid ? 1 : 0.98,
        transition: { type: "spring", delay: 0.04 },
      },
      hover: grid
        ? {}
        : {
            rotate: -28,
            x: -14,
            y: -2,
            scale: 1,
            transition: { type: "spring", delay: 0.04 },
          },
      animate: {
        rotate: 0,
        x: 0,
        y: 0,
        scale: 0.98,
        transition: { type: "spring" },
      },
      exit: {
        rotate: -20,
        x: -8,
        y: 0,
        scale: 1,
        transition: { duration: 0.25, ease: "easeIn", delay: 0.04 },
      },
    },
    {
      initial: {
        rotate: grid ? 0 : -45,
        x: grid ? 0 : 6,
        y: grid ? 0 : -4,
        scale: grid ? 1 : 0.98,
        transition: { type: "spring", delay: 0.1 },
      },
      hover: grid
        ? {}
        : {
            rotate: -18,
            x: -4,
            y: -4,
            scale: 1,
            transition: { type: "spring", delay: 0.1 },
          },
      animate: {
        rotate: 0,
        x: 0,
        y: 0,
        scale: 0.98,
      },
      exit: {
        rotate: -45,
        x: 6,
        y: -4,
        scale: 1,
        transition: { duration: 0.25, ease: "easeIn", delay: 0.1 },
      },
    },
    {
      initial: {
        rotate: grid ? 0 : -10,
        x: grid ? 0 : 4,
        y: grid ? 0 : 2,
        scale: grid ? 1 : 0.98,
        transition: { type: "spring", delay: 0.05 },
      },
      hover: grid
        ? {}
        : {
            rotate: 14,
            x: 8,
            y: 4,
            scale: 1,
            transition: { type: "spring", delay: 0.05 },
          },
      animate: {
        rotate: 0,
        x: 0,
        y: 0,
        scale: 0.98,
      },
      exit: {
        rotate: -10,
        x: 4,
        y: 2,
        scale: 1,
        transition: { duration: 0.25, ease: "easeIn", delay: 0.05 },
      },
    },
  ];

  return (
    <div
      className={cn(
        "relative size-14 gap-2 flex col-start-1 row-start-1 row-span-3",
        grid && "size-auto grid grid-cols-2",
      )}
      {...props}
    >
      {srcs.slice(0, 3).map((src, i) => (
        <motion.img
          key={i}
          src={src}
          layoutId={`${layoutIdPrefix ?? ""}${ids?.[i] ?? ""}${src}`}
          className={cn(
            "absolute top-0 left-0 rounded-md size-12 object-cover drop-shadow-lg",
            grid && "relative size-8",
            grid && srcs.length === 1 && "col-span-2",
          )}
          style={{
            zIndex: 10 + i,
          }}
          variants={imageVariants[i % imageVariants.length]}
          animate={active ? "animate" : hover ? "hover" : "initial"}
          exit="exit"
        />
      ))}
    </div>
  );
}

export function ArticleAction({
  className,
  autoHide = true,
  ...props
}: React.ComponentProps<typeof motion.div> & {
  autoHide?: boolean;
}) {
  return (
    <motion.div
      className={cn(
        "row-start-1 col-start-3 row-span-3 flex items-center gap-2 justify-center",
        "transition-all ease-in-out md:opacity-0 md:-translate-x-2 group-hover/article:opacity-100 group-hover/article:translate-x-0 [&_svg]:size-4",
        !autoHide && "!opacity-100 !translate-x-0",
        className,
      )}
      {...props}
    />
  );
}

// honestly, it's the same for now. The only diff is the image stack
export const ArticlesGroupContent = Article;
export const ArticlesGroup = ArticleList;
export const ArticlesGroupTitle = ArticleTitle;
export const ArticlesGroupSubtitle = ArticleSubtitle;
export const ArticlesGroupFooter = ArticleFooter;

export function ArticlesGroupAction({
  className,
  ...props
}: React.ComponentProps<typeof ArticleAction>) {
  return (
    <ArticleAction
      className={cn("md:!opacity-100 md:!translate-x-0", className)}
      {...props}
    />
  );
}
